import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, map, Observable, retry, throwError, timer } from 'rxjs';
import {
  CreateSessionBody,
  CreateSessionDrillBody,
  CreateSessionResponse,
  CreateSessionRunBody,
  SessionsService
} from '@session/infra';
import { RunDraft, SessionDraft, SessionDrillDraft, SessionStore } from '../../../state';
import { PlanDeletedError } from './plan-deleted.error';
import { SaveSessionCommandResult } from './save-session.command-result';

const MAX_RETRIES: number = 3;
const BASE_DELAY_MS: number = 500;

export class SaveSessionCommandHandler {
  private readonly store: SessionStore = inject(SessionStore);
  private readonly sessionsService: SessionsService = inject(SessionsService);

  execute(): Observable<SaveSessionCommandResult> {
    const draft: SessionDraft | null = this.store.snapshot();
    if (draft === null) return throwError(() => new Error('No active draft to save'));
    this.store.markSubmitPending();
    const body: CreateSessionBody = this.buildBody(draft);
    return this.sessionsService.create(body).pipe(
      retry({
        count: MAX_RETRIES,
        delay: (error: unknown, retryCount: number) => {
          if (isHttpError(error) && error.status === 409) {
            return throwError(() => new PlanDeletedError());
          }
          if (isHttpError(error) && error.status >= 400 && error.status < 500) {
            return throwError(() => error);
          }
          return timer(BASE_DELAY_MS * Math.pow(2, retryCount - 1));
        }
      }),
      catchError((error: unknown) => {
        if (isHttpError(error) && error.status === 409) {
          return throwError(() => new PlanDeletedError());
        }
        return throwError(() => error);
      }),
      map((dto: CreateSessionResponse): SaveSessionCommandResult => {
        this.store.clear();
        return { sessionId: dto.id };
      })
    );
  }

  private buildBody(draft: SessionDraft): CreateSessionBody {
    return {
      clientGeneratedId: draft.clientGeneratedId,
      startedAt: draft.startedAt,
      planId: draft.planId,
      sessionDrills: draft.sessionDrills.map(
        (sd: SessionDrillDraft): CreateSessionDrillBody => ({
          drillId: sd.drillId,
          drillName: sd.drillName,
          scoring: sd.scoring,
          shotsCount: sd.shotsCount,
          targetRuns: sd.targetRuns,
          runs: sd.runs.map(
            (r: RunDraft): CreateSessionRunBody => ({
              recordedAt: r.recordedAt,
              reps: r.reps,
              timeSeconds: r.timeSeconds,
              parTimeSeconds: r.parTimeSeconds,
              hitsA: r.hitsA,
              hitsC: r.hitsC,
              hitsD: r.hitsD,
              hitsM: r.hitsM,
              hitsNS: r.hitsNS
            })
          )
        })
      )
    };
  }
}

const isHttpError = (err: unknown): err is HttpErrorResponse => err instanceof HttpErrorResponse;
